"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  classNames,
  FlexCenter,
  Separator,
  TextBody,
  TimelineSkeleton,
} from "@opensea/ui-kit"
import { Suspense, useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { BackButton } from "../../../../../components/BackButton/BackButton"
import { CreateOrdersSummary } from "../../../../../components/CreateOrdersSummary"
import type {
  CollectionOffer,
  CreateCollectionOfferFormValues,
} from "../../types"
import { constructCollectionOffer } from "../../utils/constructCollectionOffer"
import { CreateCollectionOfferFlowTable } from "../CollectionOfferFlowTable/CreateCollectionOfferFlowTable"
import { CreateCollectionOfferTimeline } from "../CreateCollectionOfferTimeline"
import { CreateCollectionOfferFlowReview_collectionFragment } from "./CreateCollectionOfferFlowReview.fragment"

type CreateCollectionOfferFlowReviewProps = {
  collection: FragmentOf<
    typeof CreateCollectionOfferFlowReview_collectionFragment
  >
  onBack: () => void
  onSuccess?: () => void
  onError?: (error: Error) => void
}

const SEPARATOR = <Separator className="my-1" />

export const CreateCollectionOfferFlowReview = ({
  collection: collectionKey,
  onBack,
  onSuccess,
  onError,
}: CreateCollectionOfferFlowReviewProps) => {
  const t = useTranslations("wallet.CreateCollectionOfferFlowReview")
  const { getValues, control } =
    useFormContext<CreateCollectionOfferFormValues>()
  const formValues = getValues()
  const offers = useWatch({ control, name: "offers" })
  const firstOffer = offers[0]
  const collection = readFragment(
    CreateCollectionOfferFlowReview_collectionFragment,
    collectionKey,
  )
  const offerRef = useRef<CollectionOffer>(
    constructCollectionOffer({ collection, formValues }),
  )

  if (!offerRef.current) {
    return null
  }

  return (
    <>
      <BottomSheetHeader>
        <BottomSheetHeaderTitle
          className="cursor-pointer"
          onClick={() => onBack()}
        >
          <FlexCenter className="gap-3">
            <BackButton />
            <Separator className="h-4 text-secondary" orientation="vertical" />
            {firstOffer?.trait ||
            (firstOffer?.traits && firstOffer.traits.length > 0)
              ? t("reviewTraitOffer")
              : t("reviewOffer")}
          </FlexCenter>
        </BottomSheetHeaderTitle>
      </BottomSheetHeader>
      <BottomSheetBody className="mt-2 pb-0">
        <CreateCollectionOfferFlowTable
          collection={collection}
          isReview={true}
        />

        {firstOffer?.trait ||
        (firstOffer?.traits && firstOffer.traits.length > 0) ? (
          <>
            {SEPARATOR}

            <FlexCenter
              className={classNames("w-full justify-between gap-2 py-2")}
            >
              {firstOffer?.traits && firstOffer.traits.length > 0 ? (
                <>
                  <TextBody weight="semibold">
                    {t("traits", { count: firstOffer.traits.length })}
                  </TextBody>
                  <FlexCenter className="flex-wrap gap-2">
                    {firstOffer.traits.map(trait => (
                      <div key={`${trait.traitType}-${trait.value}`}>
                        {" "}
                        <TextBody color="text-secondary">
                          {trait.traitType}{" "}
                        </TextBody>
                        <TextBody weight="semibold">{trait.value}</TextBody>
                      </div>
                    ))}
                  </FlexCenter>
                </>
              ) : (
                <>
                  <TextBody weight="semibold">{t("trait")}</TextBody>
                  <div>
                    <TextBody color="text-secondary">
                      {firstOffer?.trait?.traitType}{" "}
                    </TextBody>
                    <TextBody weight="semibold">
                      {firstOffer?.trait?.value}
                    </TextBody>
                  </div>
                </>
              )}
            </FlexCenter>
          </>
        ) : null}

        {SEPARATOR}

        <CreateOrdersSummary
          adjustForFees={false}
          collection={collection}
          creatorFeeEnforced={firstOffer?.isCreatorFeeRequired}
          currencyIdentifier={{
            chainIdentifier: collection.chain.identifier,
            contractAddress: collection.chain.offerCurrency.address,
          }}
          isReview
          orderSide="offer"
          orders={offers}
          symbol={collection.chain.offerCurrency.symbol}
        />
      </BottomSheetBody>
      <BottomSheetFooter>
        <Suspense fallback={<TimelineSkeleton count={1} />}>
          <CreateCollectionOfferTimeline
            collection={collection}
            offer={offerRef.current}
            onBack={onBack}
            onError={onError}
            onSuccess={onSuccess}
          />
        </Suspense>
      </BottomSheetFooter>
    </>
  )
}
